/**
 * F-404-FE-05: CompletedNotesPage
 *
 * Lists all COMPLETED notes for the authenticated doctor.
 * - Sticky controls bar: search, date-range filter, sort toggle
 * - Client-side filter + sort (no backend round-trips for filter changes)
 * - Shared CompletedNoteCard with 5 action buttons per row
 * - Two empty states: no data vs. no filter matches
 * - Data via React Query, queryKey ['notes', 'COMPLETED'] (shared with dashboard)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DoctorLayout } from '@/components/DoctorLayout';
import { api } from '@/lib/api';
import type { NoteListItem } from './types';
import { CompletedNoteCard } from './CompletedNoteCard';
import { useCopyToEmr } from './useCopyToEmr';
import { useExportPdf } from './useExportPdf';
import { useSaveAsTxt } from './useSaveAsTxt';
import { type DateRange, getDateFilter, matchesDateFilter } from './dateFilterHelpers';
import type { NotesListResponse } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SortOrder = 'newest' | 'oldest';

// ---------------------------------------------------------------------------
// Skeleton card
// ---------------------------------------------------------------------------

function NoteSkeletonCard() {
  return (
    <div className="clinical-card p-4 flex items-center gap-3 animate-pulse">
      <div className="w-2.5 h-2.5 skeleton rounded-full flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="skeleton h-4 w-2/5 rounded" />
        <div className="skeleton h-3 w-3/5 rounded" />
        <div className="skeleton h-3 w-1/4 rounded" />
      </div>
      <div className="skeleton h-8 w-24 rounded flex-shrink-0" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

interface ToastProps {
  message: string;
  onDismiss: () => void;
}

function Toast({ message, onDismiss }: ToastProps) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4_000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-6 end-6 z-50 max-w-sm clinical-card p-4 flex items-start gap-3 shadow-clinical-lg">
      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <CheckSmallIcon />
      </div>
      <div className="flex-1">
        <p className="text-sm font-body font-medium text-clinical-charcoal">{message}</p>
      </div>
      <button
        type="button"
        className="text-clinical-text-muted hover:text-clinical-charcoal transition-colors flex-shrink-0"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <XSmallIcon />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty states
// ---------------------------------------------------------------------------

function EmptyNoData() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center gap-4 py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-clinical-cream-dark flex items-center justify-center">
        <ArchiveIcon />
      </div>
      <div>
        <h2 className="font-heading text-xl font-semibold text-clinical-charcoal">
          No completed notes yet
        </h2>
        <p className="text-base font-body text-clinical-text-muted mt-1 max-w-xs">
          Notes you complete will appear here for viewing and export.
        </p>
      </div>
      <button
        type="button"
        className="btn-secondary mt-2"
        onClick={() => navigate('/notes/pending')}
      >
        View pending notes
      </button>
    </div>
  );
}

interface EmptyFilteredProps {
  onClear: () => void;
}

function EmptyFiltered({ onClear }: EmptyFilteredProps) {
  return (
    <div className="flex flex-col items-center gap-4 py-12 text-center">
      <div className="w-12 h-12 rounded-full bg-clinical-cream-dark flex items-center justify-center">
        <SearchIcon />
      </div>
      <div>
        <h2 className="font-heading text-lg font-semibold text-clinical-charcoal">
          No notes match your filters
        </h2>
        <p className="text-sm font-body text-clinical-text-muted mt-1">
          Try adjusting your search or date range.
        </p>
      </div>
      <button type="button" className="btn-secondary" onClick={onClear}>
        Clear filters
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-note card wrapper — owns its own useCopyToEmr instance
// ---------------------------------------------------------------------------

interface NoteCardWrapperProps {
  note: NoteListItem;
  isExportingPdf: boolean;
  onExportPdf: () => void;
  isSavingTxt: boolean;
  onSaveTxt: () => void;
  onEdit: () => void;
}

function NoteCardWrapper({
  note,
  isExportingPdf,
  onExportPdf,
  isSavingTxt,
  onSaveTxt,
  onEdit,
}: NoteCardWrapperProps) {
  const { state: copyState, copy: copyToEmr } = useCopyToEmr();

  return (
    <CompletedNoteCard
      note={note}
      onCopy={() => void copyToEmr(note.id)}
      copyState={copyState}
      onExportPdf={onExportPdf}
      isExportingPdf={isExportingPdf}
      onSaveTxt={onSaveTxt}
      isSavingTxt={isSavingTxt}
      onEdit={onEdit}
    />
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CompletedNotesPage() {
  const navigate = useNavigate();

  // --- Data ---
  // Backend now applies the hospital config retention cap automatically.
  // meta.noteRetentionHours carries the effective value for the banner and
  // the defense-in-depth client-side filter.
  const { data: response, isLoading, isError, error, refetch } = useQuery<NotesListResponse>({
    queryKey: ['notes', 'COMPLETED'],
    queryFn: () => api.get<NotesListResponse>('/api/notes?status=COMPLETED'),
  });
  const notes = response?.data ?? [];
  // Defense-in-depth filter derives from the same value the backend used.
  // Falls back to 72 (system default) on the unlikely event meta is absent.
  const noteRetentionHours = response?.meta?.noteRetentionHours ?? 72;

  // --- Filter/sort state ---
  const [rawSearch, setRawSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');

  // Debounce search input 250ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(rawSearch), 250);
    return () => clearTimeout(t);
  }, [rawSearch]);

  // --- Filtered + sorted notes ---
  const filteredNotes = useMemo(() => {
    let result = notes;

    // Defense-in-depth: even though the backend caps at noteRetentionHours,
    // a stale React Query cache could still hold older entries until the next
    // refetch. This client-side filter ensures they never render.
    // Both sides derive from the same meta.noteRetentionHours value — they cannot diverge.
    // (completedAt does not exist in the DB schema; updatedAt is the proxy.)
    const capCutoff = new Date(Date.now() - noteRetentionHours * 3_600_000);
    result = result.filter((n) => {
      const d = new Date(n.completedAt ?? n.updatedAt);
      return d >= capCutoff;
    });

    const dateFilter = getDateFilter(dateRange);
    if (dateFilter) {
      result = result.filter((n) => {
        const d = new Date(n.completedAt ?? n.updatedAt);
        return matchesDateFilter(d, dateFilter);
      });
    }

    const sorted = [...result].sort((a, b) => {
      const aTime = new Date(a.completedAt ?? a.createdAt).getTime();
      const bTime = new Date(b.completedAt ?? b.createdAt).getTime();
      return sortOrder === 'newest' ? bTime - aTime : aTime - bTime;
    });

    return sorted;
  }, [notes, debouncedSearch, dateRange, sortOrder, noteRetentionHours]);

  // --- Hooks ---
  const { isExporting, exportPdf } = useExportPdf();
  const { state: saveTxtState, save: saveAsTxt } = useSaveAsTxt();
  const [toast, setToast] = useState<string | null>(null);

  // Use a ref so the notify callback in exportPdf doesn't capture stale setToast
  const setToastRef = useRef(setToast);
  setToastRef.current = setToast;

  const handleExportPdf = useCallback(
    (noteId: string) => {
      void exportPdf(noteId, (msg) => setToastRef.current(msg));
    },
    [exportPdf],
  );

  const handleSaveTxt = useCallback(
    (note: NoteListItem) => {
      void saveAsTxt(note.id, note.templateName, note.completedAt ?? note.sessionDate);
    },
    [saveAsTxt],
  );

  const handleEdit = useCallback(
    (noteId: string) => {
      navigate(`/notes/${noteId}/edit`);
    },
    [navigate],
  );

  const handleClearFilters = useCallback(() => {
    setRawSearch('');
    setDateRange('all');
    setSortOrder('newest');
  }, []);

  return (
    <DoctorLayout>
      <div className="space-y-0">
        {/* Page header */}
        <div className="pb-4">
          <h1 className="font-heading text-3xl font-semibold text-clinical-charcoal">
            Completed Notes
          </h1>
          <p className="text-base font-body text-clinical-text-muted mt-1">
            Finalized clinical notes — copy to EMR, export, or edit
          </p>
        </div>

        {/* Confidentiality banner — always shown; hidden during load to avoid flash-of-wrong-number */}
        {!isLoading && (
          <div className="mb-4 rounded-clinical border border-clinical-amber/30 bg-clinical-amber/5 px-4 py-3">
            <p className="text-sm font-body text-clinical-charcoal">
              For patient confidentiality, only notes completed in the last {noteRetentionHours} hours are available online.
            </p>
          </div>
        )}

        {/* Sticky controls bar */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-clinical-border py-3 -mx-4 px-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Search and date-range filter are permanently hidden (§2 scope decision).
                PendingNotesPage and future expansion can re-introduce them if needed. */}

            {/* Sort toggle — always visible */}
            <button
              type="button"
              className="btn-secondary sm:w-36 text-sm px-3 py-2 min-h-[44px]"
              onClick={() => setSortOrder((s) => (s === 'newest' ? 'oldest' : 'newest'))}
              aria-label={`Sort by ${sortOrder === 'newest' ? 'oldest' : 'newest'} first`}
            >
              {sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
            </button>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <NoteSkeletonCard key={i} />
            ))}
          </div>
        ) : isError ? (
          <div className="clinical-card p-6 text-center space-y-3">
            <p className="text-sm font-body text-safety-red">
              {error instanceof Error ? error.message : 'Failed to load notes.'}
            </p>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void refetch()}
            >
              Try again
            </button>
          </div>
        ) : notes.length === 0 ? (
          <EmptyNoData />
        ) : filteredNotes.length === 0 ? (
          <EmptyFiltered onClear={handleClearFilters} />
        ) : (
          <div className="space-y-3">
            {filteredNotes.map((note) => (
              <NoteCardWrapper
                key={note.id}
                note={note}
                isExportingPdf={isExporting(note.id)}
                onExportPdf={() => handleExportPdf(note.id)}
                isSavingTxt={saveTxtState === 'saving'}
                onSaveTxt={() => handleSaveTxt(note)}
                onEdit={() => handleEdit(note.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Toast notification */}
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </DoctorLayout>
  );
}

// ---------------------------------------------------------------------------
// Inline icons
// ---------------------------------------------------------------------------

function CheckSmallIcon() {
  return (
    <svg className="w-3 h-3 text-safety-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XSmallIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg className="w-8 h-8 text-clinical-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="w-6 h-6 text-clinical-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 15.803 7.5 7.5 0 0015.803 15.803z" />
    </svg>
  );
}
